/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './LegalUboDiscovery.module.scss';

type NodeType = 'subject' | 'entity' | 'unattributed' | 'person';

type NodeDef = {
  id: string;
  code: string;
  name: string;
  meta: string;
  depth: number;
  x: number;
  type: NodeType;
};

type EdgeDef = {
  from: string;
  to: string;
  pct: number;
  warn?: boolean;
};

type Step = {
  t: number;
  cap: string;
  layer?: number;
  fig?: string;
  warn?: boolean;
  lit?: string[];
  done?: boolean;
};

const ROW_H = 120;
const CARD_W = 190;
const CARD_H = 54;
const VIEW_W = 1000;
const VIEW_H = 6 * ROW_H + CARD_H + 40;

const NODES: NodeDef[] = [
  {
    id: 'subject',
    code: 'gb',
    name: 'Hallard Terminals Ltd',
    meta: 'GB · Companies House · SAMPLE-09410902',
    depth: 0,
    x: 460,
    type: 'subject',
  },
  {
    id: 'oriel',
    code: 'ie',
    name: 'Oriel Bay Holdings',
    meta: 'IE · SAMPLE-IE 488210',
    depth: 1,
    x: 200,
    type: 'entity',
  },
  {
    id: 'arden',
    code: 'nl',
    name: 'Arden Capital B.V.',
    meta: 'NL · SAMPLE-KVK 64119027',
    depth: 1,
    x: 440,
    type: 'entity',
  },
  {
    id: 'kirana',
    code: 'je',
    name: 'Kirana Trustees Ltd',
    meta: 'JE · SAMPLE-JE 112938',
    depth: 1,
    x: 740,
    type: 'entity',
  },
  {
    id: 'castelmar',
    code: 'lu',
    name: 'Castelmar S.à r.l.',
    meta: 'LU · SAMPLE-RCS 201847',
    depth: 2,
    x: 320,
    type: 'entity',
  },
  {
    id: 'selvik',
    code: 'se',
    name: 'Selvik Invest AB',
    meta: 'SE · SAMPLE-556677-1234',
    depth: 2,
    x: 660,
    type: 'entity',
  },
  {
    id: 'nordline',
    code: 'be',
    name: 'Nordline NV',
    meta: 'BE · SAMPLE-BCE 0765.432.109',
    depth: 2,
    x: 820,
    type: 'entity',
  },
  {
    id: 'verrick',
    code: 'ch',
    name: 'Verrick Capital AG',
    meta: 'CH · SAMPLE-CHE-105.837.921',
    depth: 3,
    x: 320,
    type: 'entity',
  },
  {
    id: 'meridian',
    code: 'sg',
    name: 'Meridian Holdings Pte Ltd',
    meta: 'SG · SAMPLE-ACRA 199912345K',
    depth: 3,
    x: 660,
    type: 'entity',
  },
  {
    id: 'halborn',
    code: 'de',
    name: 'Halborn Beteiligungen GmbH',
    meta: 'DE · SAMPLE-HRB 84213',
    depth: 3,
    x: 820,
    type: 'entity',
  },
  {
    id: 'corvin',
    code: 'cy',
    name: 'Corvin Holdings Ltd',
    meta: 'CY · SAMPLE-HE 298144',
    depth: 4,
    x: 320,
    type: 'entity',
  },
  {
    id: 'fjeldstad',
    code: 'no',
    name: 'Fjeldstad Kapital AS',
    meta: 'NO · SAMPLE-921 456 789',
    depth: 4,
    x: 660,
    type: 'entity',
  },
  {
    id: 'anchorage',
    code: 'pa',
    name: 'Anchorage Trust SA',
    meta: 'PA · nominee arrangement — no public register',
    depth: 4,
    x: 820,
    type: 'unattributed',
  },
  {
    id: 'trust',
    code: 'je',
    name: 'Hallard Family Trust',
    meta: 'JE · SAMPLE-Trust reg. 20194',
    depth: 5,
    x: 490,
    type: 'entity',
  },
  {
    id: 'stiftung',
    code: 'li',
    name: 'Alpenrose Stiftung',
    meta: 'LI · foundation charter — no public register',
    depth: 5,
    x: 820,
    type: 'unattributed',
  },
  {
    id: 'ehallard',
    code: 'gb',
    name: 'E. Hallard',
    meta: 'Natural person · resident JE',
    depth: 6,
    x: 655,
    type: 'person',
  },
];

const EDGES: EdgeDef[] = [
  { from: 'oriel', to: 'subject', pct: 61 },
  { from: 'arden', to: 'subject', pct: 22 },
  { from: 'kirana', to: 'subject', pct: 88 },
  { from: 'castelmar', to: 'oriel', pct: 100 },
  { from: 'castelmar', to: 'arden', pct: 82 },
  { from: 'selvik', to: 'kirana', pct: 45 },
  { from: 'nordline', to: 'kirana', pct: 18 },
  { from: 'verrick', to: 'castelmar', pct: 100 },
  { from: 'meridian', to: 'selvik', pct: 85 },
  { from: 'halborn', to: 'nordline', pct: 62 },
  { from: 'corvin', to: 'verrick', pct: 100 },
  { from: 'fjeldstad', to: 'meridian', pct: 60 },
  { from: 'anchorage', to: 'halborn', pct: 100, warn: true },
  { from: 'trust', to: 'corvin', pct: 75 },
  { from: 'trust', to: 'fjeldstad', pct: 25 },
  { from: 'stiftung', to: 'anchorage', pct: 100, warn: true },
  { from: 'ehallard', to: 'trust', pct: 82 },
  { from: 'ehallard', to: 'stiftung', pct: 18, warn: true },
];

const PATH_A = ['subject', 'oriel', 'arden', 'castelmar', 'verrick', 'corvin', 'trust', 'ehallard'];
const PATH_B = ['subject', 'kirana', 'selvik', 'meridian', 'fjeldstad', 'trust', 'ehallard'];
const PATH_C = ['subject', 'kirana', 'nordline', 'halborn', 'anchorage', 'stiftung', 'ehallard'];
const PATH_ALL = Array.from(new Set([...PATH_A, ...PATH_B, ...PATH_C]));

const STEPS: Step[] = [
  { t: 0, cap: 'Subject — Hallard Terminals Ltd, Companies House', layer: 0 },
  { t: 700, cap: 'Layer 1 — three direct holders in Ireland, the Netherlands and Jersey', layer: 1 },
  { t: 1400, cap: 'Layer 2 — Luxembourg, Sweden, Belgium', layer: 2 },
  { t: 2100, cap: 'Layer 3 — Switzerland, Singapore, Germany', layer: 3 },
  { t: 2800, cap: 'Layer 4 — Cyprus, Norway, Panama', layer: 4 },
  { t: 3500, cap: 'Layer 5 — Jersey trust, Liechtenstein foundation', layer: 5 },
  { t: 4200, cap: 'Layer 6 — natural person reached: E. Hallard', layer: 6 },
  { t: 5100, cap: '16 entities resolved across 14 jurisdictions', done: true },
  { t: 6200, cap: 'Path 1 of 3 — Oriel Bay, Arden Capital, Castelmar', fig: '37.52%', lit: PATH_A },
  { t: 8000, cap: 'Path 2 of 3 — Selvik and Kirana', fig: '43.89%', lit: PATH_B },
  { t: 9600, cap: 'Path 3 of 3 — Kirana into Nordline', fig: '46.41%', lit: PATH_C },
  { t: 11200, cap: 'Effective interest, E. Hallard, all paths combined', fig: '46.41%', lit: PATH_ALL },
  {
    t: 13000,
    cap: 'Two branches end at registers that do not publish',
    fig: '27.56%',
    warn: true,
    lit: ['anchorage', 'stiftung'],
  },
  { t: 15000, cap: 'Full structure — 6 layers to a natural person' },
];

const TOTAL = 16400;

const STATS = [
  { n: '16', l: 'Entities resolved' },
  { n: '14', l: 'Jurisdictions traversed' },
  { n: '06', l: 'Layers to a natural person' },
  { n: '46.4%', l: 'Effective interest, E. Hallard' },
  { n: '27.6%', l: 'Unattributed — two branches end at registers that do not publish', warn: true },
];

const edgePath = (from: NodeDef, to: NodeDef) => {
  const x1 = from.x;
  const y1 = from.depth * ROW_H;
  const x2 = to.x;
  const y2 = to.depth * ROW_H + CARD_H;
  const midY = (y1 + y2) / 2;
  return `M ${x1} ${y1} C ${x1} ${midY} ${x2} ${midY} ${x2} ${y2}`;
};

export const LegalUboDiscovery = () => {
  const { t } = useInt();
  const cardRef = useRef<HTMLDivElement>(null);
  const inView = useIntersection(cardRef, { threshold: 0.2, continuous: true });

  const [revealedLayer, setRevealedLayer] = useState(-1);
  const [caption, setCaption] = useState(STEPS[0].cap);
  const [figure, setFigure] = useState('');
  const [figureWarn, setFigureWarn] = useState(false);
  const [litNodes, setLitNodes] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const rafRef = useRef<number | null>(null);
  const t0Ref = useRef(0);
  const elapsedRef = useRef(0);
  const curRef = useRef(-1);
  const playingRef = useRef(false);
  const seenRef = useRef(false);

  const applyStep = useCallback((i: number, jumped: boolean) => {
    const step = STEPS[i];

    if (jumped) {
      let layer = -1;
      for (let k = 0; k <= i; k += 1) {
        if (STEPS[k].layer !== undefined) {
          layer = STEPS[k].layer as number;
        }
      }
      setRevealedLayer(layer);
    } else if (step.layer !== undefined) {
      setRevealedLayer(step.layer);
    }

    setCaption(step.cap);
    setFigure(step.fig ?? '');
    setFigureWarn(!!step.warn);
    setLitNodes(step.lit ?? []);

    for (let k = 0; k <= i; k += 1) {
      if (STEPS[k].done) {
        setDone(true);
      }
    }
  }, []);

  const stop = useCallback(() => {
    playingRef.current = false;
    setPlaying(false);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
  }, []);

  const frame = useCallback(
    (now: number) => {
      if (!playingRef.current) {
        return;
      }

      elapsedRef.current += now - t0Ref.current;
      t0Ref.current = now;
      setProgress(Math.min(100, (elapsedRef.current / TOTAL) * 100));

      let idx = 0;
      for (let i = 0; i < STEPS.length; i += 1) {
        if (elapsedRef.current >= STEPS[i].t) {
          idx = i;
        }
      }

      if (idx !== curRef.current) {
        const jumped = idx > curRef.current + 1;
        curRef.current = idx;
        applyStep(idx, jumped);
      }

      if (elapsedRef.current >= TOTAL) {
        stop();
        return;
      }

      rafRef.current = requestAnimationFrame(frame);
    },
    [applyStep, stop],
  );

  const start = useCallback(() => {
    playingRef.current = true;
    setPlaying(true);
    t0Ref.current = performance.now();
    rafRef.current = requestAnimationFrame(frame);
  }, [frame]);

  const restart = useCallback(() => {
    stop();
    elapsedRef.current = 0;
    curRef.current = -1;
    setDone(false);
    setRevealedLayer(-1);
    setLitNodes([]);
    setFigure('');
    setFigureWarn(false);
    setProgress(0);
    applyStep(0, false);
    start();
  }, [stop, applyStep, start]);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setReducedMotion(true);
      setRevealedLayer(6);
      setDone(true);
      setProgress(100);
      setCaption('Full structure — 6 layers, 14 jurisdictions');
    }
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      return undefined;
    }

    if (inView) {
      if (!seenRef.current) {
        seenRef.current = true;
        restart();
      } else if (!playingRef.current && elapsedRef.current < TOTAL) {
        start();
      }
    } else {
      stop();
    }

    return () => stop();
  }, [inView, reducedMotion, restart, start, stop]);

  return (
    <Section className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.head}>
          <TitleBlock align="left" title={t('Trace the corporate tree from the bottom to the very top.')} />
        </div>

        <div className={styles.card} ref={cardRef}>
          <div className={styles.top}>
            <span className={styles.title}>Hallard Terminals Ltd — resolved ownership</span>
            <span className={styles.sub}>SAMPLE data · 14 registries</span>
            <button
              type="button"
              className={styles.btn}
              onClick={() => {
                if (playing) {
                  stop();
                } else if (elapsedRef.current >= TOTAL) {
                  restart();
                } else {
                  start();
                }
              }}
            >
              {playing ? 'Pause' : 'Play'}
            </button>
            <button type="button" className={styles.btn} onClick={restart}>
              Replay
            </button>
          </div>

          <div className={styles.bar}>
            <i style={{ width: `${progress}%` }} />
          </div>

          <div className={styles.cap}>
            <span>{caption}</span>
            {figure && <span className={cn(styles.fig, figureWarn && styles.figWarn)}>{figure}</span>}
          </div>

          <div className={styles.scroll}>
            <div className={styles.graph}>
              <div
                className={cn(styles.canvas, done && styles.graphDone)}
                style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }}
              >
                <svg className={styles.edges} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="none">
                  {EDGES.map((edge) => {
                    const from = NODES.find((n) => n.id === edge.from) as NodeDef;
                    const to = NODES.find((n) => n.id === edge.to) as NodeDef;
                    const on = revealedLayer >= from.depth;
                    const lit = litNodes.includes(from.id) && litNodes.includes(to.id);
                    return (
                      <path
                        className={cn(
                          styles.edge,
                          on && styles.edgeOn,
                          lit && styles.edgeLit,
                          edge.warn && styles.edgeWarn,
                        )}
                        d={edgePath(from, to)}
                        key={`${edge.from}-${edge.to}`}
                      />
                    );
                  })}
                </svg>

                {EDGES.map((edge) => {
                  const from = NODES.find((n) => n.id === edge.from) as NodeDef;
                  const to = NODES.find((n) => n.id === edge.to) as NodeDef;
                  const on = revealedLayer >= from.depth;
                  const y1 = from.depth * ROW_H;
                  const y2 = to.depth * ROW_H + CARD_H;
                  return (
                    <span
                      className={cn(styles.pct, on && styles.pctOn, edge.warn && styles.pctWarn)}
                      style={{
                        left: `${((from.x + to.x) / 2 / VIEW_W) * 100}%`,
                        top: `${((y1 + y2) / 2 / VIEW_H) * 100}%`,
                      }}
                      key={`pct-${edge.from}-${edge.to}`}
                    >
                      {edge.pct}%
                    </span>
                  );
                })}

                {NODES.map((node) => (
                  <div
                    className={cn(
                      styles.node,
                      styles[`node${node.type[0].toUpperCase()}${node.type.slice(1)}`],
                      node.depth <= revealedLayer && styles.nodeOn,
                      litNodes.includes(node.id) && styles.nodeLit,
                    )}
                    style={{
                      left: `${(node.x / VIEW_W) * 100}%`,
                      top: `${((node.depth * ROW_H) / VIEW_H) * 100}%`,
                      width: `${(CARD_W / VIEW_W) * 100}%`,
                    }}
                    key={node.id}
                  >
                    <Flag code={node.code} className={styles.nodeFlag} rounded />
                    <span className={styles.nodeName}>{node.name}</span>
                    <span className={styles.nodeMeta}>{node.meta}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={cn(styles.stats, done && styles.statsOn)}>
            {STATS.map((stat, i) => (
              <div
                className={cn(styles.stat, stat.warn && styles.statWarn)}
                style={{ '--d': `${i * 70}ms` } as CSSProperties}
                key={stat.l}
              >
                <div className={styles.statN}>{stat.n}</div>
                <div className={styles.statL}>{stat.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};
